import { useState, useRef, useEffect, useMemo } from 'react';
import { X, Send, MessageSquare, User, Shield, PanelLeftClose, PanelLeftOpen, Search, Phone, Paperclip } from 'lucide-react';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { useDrivers } from '../../hooks/useDrivers';
import { getMessageAttachmentDownloadUrl } from '../../lib/api/endpoints';
import type { ConversationDto, MessageAttachmentDto } from '../../lib/api/endpoints';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { EmergencyCallModal } from '../alerts/EmergencyCallModal';
import { validarAdjuntoChat, mensajeErrorChat, formatearBytes } from '../../utils/chat';
import { Spinner } from '../ui/Spinner';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

// Mismo corte que `.chat-panel` a pantalla completa en `index.css`: ahí el panel
// ocupa todo el ancho y lista + mensajes no caben a la vez.
const NARROW_QUERY = '(max-width: 480px)';
const CONTACTS_COLLAPSED_KEY = 'paitron.chat.contactsCollapsed';

// La preferencia es una comodidad por navegador, nunca un dato: si el
// almacenamiento no está disponible (modo privado, datos bloqueados) el panel
// funciona igual, solo que no recuerda cómo lo dejó el usuario.
function readContactsCollapsed(): boolean {
  try { return localStorage.getItem(CONTACTS_COLLAPSED_KEY) === '1'; } catch { return false; }
}
function writeContactsCollapsed(collapsed: boolean): void {
  try { localStorage.setItem(CONTACTS_COLLAPSED_KEY, collapsed ? '1' : '0'); } catch { /* sin almacenamiento */ }
}

const ROLE_LABEL: Record<string, string> = {
  ADMINISTRADOR: 'Administrador',
  SUPERVISOR: 'Supervisor',
  OPERADOR: 'Operador',
  EJECUTIVO: 'Ejecutivo',
};

// FE-21: el título ya no sale de comparar nombres (dos homónimos compartían
// conversación). Si NINGÚN participante soy yo, es el Administrador leyendo
// una conversación ajena por su lectura sin filtro (§3.5 de EP-55) — ahí se
// muestran los dos nombres, no "el mío".
function conversationTitle(conversation: ConversationDto, myUserId: string | undefined): string {
  const others = conversation.participants.filter(p => p.userId !== myUserId);
  if (others.length === conversation.participants.length) {
    return conversation.participants.map(p => p.displayName).join(' · ');
  }
  return others[0]?.displayName ?? '';
}

// El "otro" participante — usado tanto para el título como para la lista.
// Con un Administrador leyendo por oversight ninguno de los dos soy yo; ese
// caso lo resuelve `conversationTitle` aparte, aquí basta el primero.
function otherParticipant(conversation: ConversationDto, myUserId: string | undefined) {
  return conversation.participants.find(p => p.userId !== myUserId) ?? conversation.participants[0];
}

function ChatAttachmentChip({ messageId, attachment }: { messageId: string; attachment: MessageAttachmentDto }) {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const isImage = attachment.contentType.startsWith('image/');
  const isVideo = attachment.contentType.startsWith('video/');

  // La URL es una capacidad temporal que no se persiste (ADR-014): se pide
  // AL ABRIR, nunca al listar los mensajes.
  const open = async () => {
    setErrorMsg(null);
    setLoading(true);
    try {
      const { url } = await getMessageAttachmentDownloadUrl(messageId, attachment.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setErrorMsg(mensajeErrorChat(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: '0.25rem' }}>
      <button
        type="button"
        onClick={() => void open()}
        disabled={loading}
        style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '0.375rem 0.625rem', fontSize: '11px', color: 'var(--text-primary)', cursor: 'pointer' }}
      >
        <Paperclip size={12} />
        {loading ? 'Abriendo…' : isImage ? 'Ver foto' : isVideo ? 'Ver video' : 'Ver adjunto'}
        <span style={{ color: 'var(--text-muted)' }}>· {formatearBytes(attachment.sizeBytes)}</span>
      </button>
      {errorMsg && <p style={{ margin: '0.25rem 0 0', fontSize: '10px', color: '#EF4444' }}>{errorMsg}</p>}
    </div>
  );
}

export function ChatPanel({ isOpen, onClose }: ChatPanelProps) {
  const { user } = useAuth();
  const {
    contacts, conversations, isLoading, activeConversationId, activeMessages,
    isLoadingMessages, hasMoreMessages, canSendInActive,
    selectContact, selectConversation, loadOlderMessages, sendText, sendAttachment, markActiveRead,
  } = useChat();
  const { drivers } = useDrivers();
  const [text, setText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // La columna de contactos se pliega. En escritorio la elección se recuerda; en
  // pantalla angosta no (allí se pliega sola al elegir a alguien y siempre
  // arranca abierta, porque sin lista no hay a quién elegir).
  const isNarrow = useMediaQuery(NARROW_QUERY);
  const [contactsOpen, setContactsOpen] = useState(() => isNarrow || !readContactsCollapsed());
  const [showCallModal, setShowCallModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isSupervisor = user?.role === 'supervisor' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';
  const isOperator = user?.role === 'operator';
  const myUserId = user?.id;

  const activeConversation = conversations.find(c => c.id === activeConversationId) ?? null;
  const activeOtherParticipant = activeConversation?.participants.find(p => p.userId !== myUserId);
  const activeContactName = activeConversation ? conversationTitle(activeConversation, myUserId) : '';
  const activeIsOversight = isAdmin && !!activeConversation && !canSendInActive;

  // El plate solo existe si el "otro" es un conductor (`ChatContact.driverId`
  // → `Driver.userId`, ambos apuntan a `app_user`, nunca al nombre).
  const activeDriverPlate = activeOtherParticipant?.driverId
    ? drivers.find(d => d.userId === activeOtherParticipant.userId)?.assignedVehiclePlate
    : undefined;
  const activeDriverPhone = activeOtherParticipant?.driverId
    ? drivers.find(d => d.userId === activeOtherParticipant.userId)?.phone
    : undefined;

  const canCallCurrentContact = isSupervisor || isOperator ? !!activeConversation && !activeIsOversight : false;

  // Contactos con quien iniciar (Supervisor/Admin) — la matriz por rol ya la
  // resolvió el backend en GET /chat/contacts.
  const filteredContacts = useMemo(() => contacts.filter(c =>
    c.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.subunitName ?? '').toLowerCase().includes(searchQuery.toLowerCase()),
  ), [contacts, searchQuery]);

  // El Operador no elige de una lista de contactos —GET /chat/contacts le
  // devuelve vacío a propósito, no inicia— ve directamente las conversaciones
  // que otros ya abrieron con él. 🚨 Puede tener MÁS DE UNA (su supervisor Y
  // el Administrador, por ejemplo): sin selector, la app elegía la primera
  // en silencio y el operador no tenía forma de saber a cuál le estaba
  // escribiendo ni de cambiarse a la otra.
  const operatorConversations = conversations;
  const filteredOperatorConversations = useMemo(() => operatorConversations.filter(c =>
    otherParticipant(c, myUserId).displayName.toLowerCase().includes(searchQuery.toLowerCase()),
  ), [operatorConversations, searchQuery, myUserId]);
  const showSidebar = isSupervisor || (isOperator && operatorConversations.length > 1);
  // `showSidebar` = ¿este rol tiene lista?; `showContacts` = ¿está desplegada?
  const showContacts = showSidebar && contactsOpen;

  const toggleContacts = () => {
    const next = !contactsOpen;
    setContactsOpen(next);
    if (!isNarrow) writeContactsCollapsed(!next);
  };
  // Elegir a alguien en móvil cierra la lista para dejar la conversación a la
  // vista; en escritorio la lista se queda como el usuario la dejó.
  const afterPickingContact = () => { if (isNarrow) setContactsOpen(false); };

  // Las que el Administrador VE por su lectura sin filtro pero no aparecen
  // como contacto (no es él quien las abrió) — sin esto, `canSendInActive`
  // nunca podía valer `false` desde la propia UI: no había forma de
  // seleccionar una conversación ajena para llegar a probarlo.
  const oversightConversations = useMemo(() => (isAdmin
    ? conversations.filter(c => !c.participants.some(p => p.userId === myUserId))
    : []), [isAdmin, conversations, myUserId]);

  // Selecciona el primer contacto/conversación disponible en cuanto llegan
  // los datos reales. Con el sidebar visible, el operador puede cambiarse a
  // otra después — esto es solo el punto de partida.
  useEffect(() => {
    if (activeConversationId) return;
    if (isSupervisor && contacts.length > 0) {
      void selectContact(contacts[0]);
    } else if (isOperator && conversations.length > 0) {
      selectConversation(conversations[0].id);
    }
  }, [isSupervisor, isOperator, contacts, conversations, activeConversationId, selectContact, selectConversation]);

  useEffect(() => {
    if (isOpen && activeConversationId) {
      markActiveRead();
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isOpen, activeConversationId, activeMessages.length, markActiveRead]);

  useEffect(() => {
    // Lo despachan AlertCard / AlertExpandedModal / AlertAttendModal con
    // `new CustomEvent('open-chat', { detail: { driverId, driverName } })`.
    // Se resuelve por `driverId` — dos conductores homónimos ya no comparten
    // conversación por esta vía tampoco.
    const handleOpenChat = (event: Event) => {
      const { driverId, driverName } = (event as CustomEvent<{ driverId?: string; driverName?: string }>).detail ?? {};
      const contact = driverId
        ? contacts.find(c => c.driverId === driverId)
        : contacts.find(c => c.displayName === driverName);
      if (contact) {
        void selectContact(contact);
        if (isNarrow) setContactsOpen(false);
      }
    };
    window.addEventListener('open-chat', handleOpenChat);
    return () => window.removeEventListener('open-chat', handleOpenChat);
  }, [contacts, selectContact, isNarrow]);

  const handlePickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const problema = validarAdjuntoChat(file);
    if (problema) { setAttachError(problema); return; }
    setAttachError(null);
    setPendingFile(file);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeConversationId || !canSendInActive) return;
    if (!text.trim() && !pendingFile) return;
    setSendError(null);

    try {
      if (pendingFile) {
        setUploadProgress(0);
        await sendAttachment(pendingFile, text.trim() || undefined, setUploadProgress);
        setPendingFile(null);
      } else {
        await sendText(text.trim());
      }
      setText('');
    } catch (err) {
      setSendError(mensajeErrorChat(err));
    } finally {
      setUploadProgress(null);
    }
  };

  const formatTime = (ts: string) => {
    try { return format(parseISO(ts), 'HH:mm', { locale: es }); } catch { return ''; }
  };

  return (
    <div className={`chat-panel ${isOpen ? 'chat-panel--open' : ''}`} style={{ width: showContacts ? '480px' : '340px' }}>

      {/* Top Header */}
      <div className="chat-panel__header">
        {showSidebar && (
          <button
            type="button"
            onClick={toggleContacts}
            aria-label={contactsOpen ? 'Ocultar contactos' : 'Mostrar contactos'}
            aria-expanded={contactsOpen}
            title={contactsOpen ? 'Ocultar contactos' : 'Mostrar contactos'}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.25rem', marginRight: '0.25rem', display: 'flex' }}
          >
            {contactsOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
          </button>
        )}
        <div className="chat-panel__contact">
          <div className="chat-panel__contact-avatar">
            {activeContactName.charAt(0)}
          </div>
          <div>
            <p className="chat-panel__contact-name">{activeContactName || (isOperator ? 'Centro de Control' : '')}</p>
            <p className="chat-panel__contact-role">
              {activeIsOversight
                ? 'Solo lectura · no participas en esta conversación'
                : activeDriverPlate
                ? `Vehículo: ${activeDriverPlate}`
                : activeOtherParticipant?.role
                ? ROLE_LABEL[activeOtherParticipant.role]
                : ''}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {canCallCurrentContact && (
            <button
              onClick={() => setShowCallModal(true)}
              style={{
                width: '32px', height: '32px', borderRadius: '50%',
                background: '#10B981', color: '#FFF', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
                transition: 'transform 0.15s ease'
              }}
              title={`Iniciar llamada directa con ${activeContactName}`}
              aria-label="Llamar"
            >
              <Phone size={15} />
            </button>
          )}
          <button className="chat-panel__close" onClick={onClose} aria-label="Cerrar chat">
            <X size={18} />
          </button>
        </div>
      </div>

      <EmergencyCallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        driverName={activeContactName}
        driverPhone={activeDriverPhone}
        vehiclePlate={activeDriverPlate ?? ''}
      />

      {/* Main Body: Multi-contact sidebar for Supervisor / Admin */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {showSidebar && (
          <div style={{
            width: '188px',
            borderRight: '1px solid var(--border)',
            background: 'var(--bg-raised)',
            display: showContacts ? 'flex' : 'none',
            flexDirection: 'column',
            flexShrink: 0
          }}>
            <div style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'var(--bg-surface)', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                <Search size={13} style={{ color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  placeholder={isSupervisor ? 'Buscar contacto...' : 'Buscar conversación...'}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ width: '100%', border: 'none', background: 'transparent', fontSize: '11px', color: 'var(--text-primary)', outline: 'none' }}
                />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {isSupervisor && filteredContacts.map(c => {
                const conversation = c.conversationId ? conversations.find(conv => conv.id === c.conversationId) : undefined;
                const isSelected = conversation?.id === activeConversationId;
                return (
                  <button
                    key={c.userId}
                    onClick={() => { void selectContact(c); afterPickingContact(); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.625rem 0.75rem', border: 'none',
                      borderBottom: '1px solid var(--border)',
                      background: isSelected ? 'var(--accent-glow)' : 'transparent',
                      color: isSelected ? 'var(--accent)' : 'var(--text-primary)',
                      textAlign: 'left', cursor: 'pointer',
                      transition: 'background var(--trans-fast)'
                    }}
                  >
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: isSelected ? 'var(--accent)' : 'var(--border)', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>
                      {c.displayName.charAt(0)}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{ margin: 0, fontSize: '12px', fontWeight: isSelected ? 700 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.displayName}</p>
                      <p style={{ margin: 0, fontSize: '10px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conversation?.lastMessage ? (conversation.lastMessage.body ?? '📎 Adjunto') : (c.subunitName ?? ROLE_LABEL[c.role])}
                      </p>
                    </div>
                    {conversation && conversation.unreadCount > 0 && conversation.id !== activeConversationId && (
                      <span style={{ minWidth: '18px', height: '18px', borderRadius: '9px', background: 'var(--accent)', color: '#FFF', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', flexShrink: 0 }}>
                        {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
                      </span>
                    )}
                  </button>
                );
              })}
              {!isSupervisor && filteredOperatorConversations.map(conv => {
                const other = otherParticipant(conv, myUserId);
                const isSelected = conv.id === activeConversationId;
                return (
                  <button
                    key={conv.id}
                    onClick={() => { selectConversation(conv.id); afterPickingContact(); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.625rem 0.75rem', border: 'none',
                      borderBottom: '1px solid var(--border)',
                      background: isSelected ? 'var(--accent-glow)' : 'transparent',
                      color: isSelected ? 'var(--accent)' : 'var(--text-primary)',
                      textAlign: 'left', cursor: 'pointer',
                      transition: 'background var(--trans-fast)'
                    }}
                  >
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: isSelected ? 'var(--accent)' : 'var(--border)', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>
                      {other.displayName.charAt(0)}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{ margin: 0, fontSize: '12px', fontWeight: isSelected ? 700 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{other.displayName}</p>
                      <p style={{ margin: 0, fontSize: '10px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conv.lastMessage ? (conv.lastMessage.body ?? '📎 Adjunto') : (other.role ? ROLE_LABEL[other.role] : '')}
                      </p>
                    </div>
                    {conv.unreadCount > 0 && conv.id !== activeConversationId && (
                      <span style={{ minWidth: '18px', height: '18px', borderRadius: '9px', background: 'var(--accent)', color: '#FFF', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', flexShrink: 0 }}>
                        {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                      </span>
                    )}
                  </button>
                );
              })}
              {isSupervisor && filteredContacts.length === 0 && (
                <p style={{ margin: 0, padding: '1rem', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center' }}>No hay contactos</p>
              )}

              {oversightConversations.length > 0 && (
                <>
                  <p style={{ margin: 0, padding: '0.5rem 0.75rem 0.25rem', fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', borderTop: '1px solid var(--border)' }}>
                    Solo lectura
                  </p>
                  {oversightConversations.map(conv => {
                    const title = conversationTitle(conv, myUserId);
                    const isSelected = conv.id === activeConversationId;
                    return (
                      <button
                        key={conv.id}
                        onClick={() => { selectConversation(conv.id); afterPickingContact(); }}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.625rem 0.75rem', border: 'none',
                          borderBottom: '1px solid var(--border)',
                          background: isSelected ? 'var(--accent-glow)' : 'transparent',
                          color: isSelected ? 'var(--accent)' : 'var(--text-secondary)',
                          textAlign: 'left', cursor: 'pointer',
                        }}
                      >
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--border)', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>
                          {title.charAt(0)}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p style={{ margin: 0, fontSize: '12px', fontWeight: isSelected ? 700 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</p>
                          <p style={{ margin: 0, fontSize: '10px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {conv.lastMessage ? (conv.lastMessage.body ?? '📎 Adjunto') : ''}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        )}

        {/* Right Messages Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          <div className="chat-panel__messages">
            {hasMoreMessages && (
              <button
                onClick={() => void loadOlderMessages()}
                style={{ alignSelf: 'center', margin: '0.5rem auto', fontSize: '11px', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Cargar mensajes anteriores
              </button>
            )}

            {isOperator && operatorConversations.length === 0 && !isLoading && (
              <div className="chat-panel__empty">
                <MessageSquare size={32} style={{ opacity: 0.3 }} />
                <p>Tu supervisor todavía no te ha escrito.</p>
              </div>
            )}

            {activeConversationId && activeMessages.length === 0 && !isLoadingMessages && (
              <div className="chat-panel__empty">
                <MessageSquare size={32} style={{ opacity: 0.3 }} />
                <p>No hay mensajes aún con este contacto.</p>
              </div>
            )}

            {activeMessages.map(msg => {
              const isMine = msg.senderUserId === myUserId;
              const senderRole = activeConversation?.participants.find(p => p.userId === msg.senderUserId)?.role;
              return (
                <div
                  key={msg.id}
                  className={`chat-bubble ${isMine ? 'chat-bubble--mine' : 'chat-bubble--theirs'}`}
                >
                  {!isMine && (
                    <div className="chat-bubble__avatar">
                      {senderRole === 'OPERADOR' ? <User size={12} /> : <Shield size={12} />}
                    </div>
                  )}
                  <div className="chat-bubble__body">
                    {!isMine && (
                      <span className="chat-bubble__sender">{msg.senderDisplayName}</span>
                    )}
                    {msg.body && <p className="chat-bubble__text">{msg.body}</p>}
                    {msg.attachment && <ChatAttachmentChip messageId={msg.id} attachment={msg.attachment} />}
                    <span className="chat-bubble__time">{formatTime(msg.sentAt)}</span>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {activeIsOversight ? (
            <p style={{ margin: 0, padding: '0.625rem 0.875rem', fontSize: '11px', color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
              Estás viendo esta conversación como Administrador. No puedes escribir en una conversación en la que no participas.
            </p>
          ) : (
            <>
              {attachError && <p style={{ margin: '0.25rem 0.875rem 0', fontSize: '11px', color: '#EF4444' }}>{attachError}</p>}
              {sendError && <p style={{ margin: '0.25rem 0.875rem 0', fontSize: '11px', color: '#EF4444' }}>{sendError}</p>}
              {pendingFile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', margin: '0.375rem 0.875rem 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  <Paperclip size={12} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{pendingFile.name} · {formatearBytes(pendingFile.size)}</span>
                  {uploadProgress === null && (
                    <button type="button" onClick={() => setPendingFile(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }} aria-label="Quitar adjunto">
                      <X size={12} />
                    </button>
                  )}
                </div>
              )}
              {uploadProgress !== null && (
                <div style={{ height: '3px', margin: '0.375rem 0.875rem 0', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round(uploadProgress * 100)}%`, background: 'var(--accent)', transition: 'width 0.15s ease' }} />
                </div>
              )}

              {/* Input Form */}
              <form className="chat-panel__input-row" onSubmit={e => void handleSend(e)}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/heic,image/webp,video/mp4"
                  style={{ display: 'none' }}
                  onChange={handlePickFile}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadProgress !== null}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0.375rem' }}
                  aria-label="Adjuntar foto o video"
                >
                  <Paperclip size={16} />
                </button>
                <input
                  id="chat-input"
                  className="chat-panel__input"
                  type="text"
                  placeholder={`Enviar mensaje a ${activeContactName.split(' ')[0]}...`}
                  value={text}
                  onChange={e => setText(e.target.value)}
                  disabled={uploadProgress !== null}
                  autoComplete="off"
                />
                <button
                  type="submit"
                  className="chat-panel__send"
                  disabled={(!text.trim() && !pendingFile) || uploadProgress !== null}
                  aria-label="Enviar mensaje"
                >
                  {uploadProgress !== null ? <Spinner size="sm" /> : <Send size={16} />}
                </button>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
